import React, { useEffect, useId, useMemo, useState } from "react"
import style from "./InputPhoto.module.css"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { faCropSimple, faImage, faTrashCan, faXmark } from "@fortawesome/free-solid-svg-icons"
import Button, { ButtonIcon } from "../Button/Button"
import { useSelectPhoto } from "../../hooks/useSelectPhoto"
import Animate, { AnimationTypes } from "../Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../Titles/Titles"
import THEME from "../../configuration/Theme"
import { useTranslation } from "react-i18next"
import { IMAGE_PROFILES, ImageProfileKey } from "../../constants/imageProfiles"
import ModalPortal from "../Modal/ModalPortal"
import { SIZE } from "../../configuration/Sizes"
import { buildCroppedFile, CropState } from "./InputPhotoCropModal"

interface InputPhotoProps {
  initialPhoto?: string | null
  handleFileSelected: (file: File | null) => void
  disable?: boolean
  iconColor?: string
  color?: string
  iconButton?: boolean
  titleButton?: string
  imageProfile?: ImageProfileKey
}

const REMOVE_IMAGE_MARKER_NAME = "__remove_icon__"

const createRemoveImageMarker = () => new File(["remove-image"], REMOVE_IMAGE_MARKER_NAME, { type: "text/plain" })

const InputPhotoCropModal: React.FC<{
  cropState: CropState
  profile: { width: number; height: number; label: string }
  onCancel: () => void
  onConfirm: (file: File | null) => void
}> = ({ cropState, onCancel, onConfirm, profile }) => {
  const { t } = useTranslation("inputPhoto")

  const [zoom, setZoom] = useState(1)

  const [horizontalPosition, setHorizontalPosition] = useState(50)

  const [verticalPosition, setVerticalPosition] = useState(50)

  const [previewUrl, setPreviewUrl] = useState<string>(cropState.src)

  const ratio = useMemo(() => profile.width / profile.height, [profile.height, profile.width])

  useEffect(() => {
    let isMounted = true

    const updatePreview = async () => {
      const file = await buildCroppedFile(cropState.file, profile, zoom, horizontalPosition, verticalPosition)

      if (!isMounted || !file) {
        return
      }

      const url = URL.createObjectURL(file)
      setPreviewUrl((old) => {
        if (old.startsWith("blob:")) {
          URL.revokeObjectURL(old)
        }

        return url
      })
    }

    updatePreview()

    return () => {
      isMounted = false
    }
  }, [cropState.file, horizontalPosition, profile, verticalPosition, zoom])

  useEffect(() => {
    return () => {
      if (previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  const confirmSelection = async () => {
    const file = await buildCroppedFile(cropState.file, profile, zoom, horizontalPosition, verticalPosition)
    onConfirm(file)
  }

  const confirmDefaultSelection = async () => {
    const file = await buildCroppedFile(cropState.file, profile, 1, 50, 50)
    onConfirm(file)
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <section className={`${defaultStyle.alert1} ${style.cropModalPopup}`}>
            <TitlePageResponsiveIconSmall
              title={t("label_crop_title")}
              icon={faCropSimple}
              boxShadow="none"
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faXmark}
                    onClick={onCancel}
                    size="small"
                    descriptionKey="interactionDescriptions:components.inputPhoto.inputPhoto.button.onCancel"
                  />
                </div>
              }
            />

            <p className={style.cropSizeHint}>{t("label_drag_tip")}</p>

            <div className={style.cropPreview} style={{ aspectRatio: `${ratio}` }}>
              <img src={previewUrl} alt={t("label_crop_preview_alt")} className={style.cropPreviewImage} />
            </div>

            <div className={defaultStyle.divisionBar}></div>

            <div className={style.cropControls}>
              <label>
                <span className={style.sliderLabel}>
                  <span>{t("label_zoom")}</span>
                  <b>{zoom.toFixed(1)}x</b>
                </span>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  data-interaction-description-key="interactionDescriptions:components.inputPhoto.inputPhoto.input.input"
                />
              </label>

              <label>
                <span className={style.sliderLabel}>
                  <span>{t("label_horizontal")}</span>
                  <b>{Math.round(horizontalPosition)}%</b>
                </span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={horizontalPosition}
                  onChange={(e) => setHorizontalPosition(Number(e.target.value))}
                  data-interaction-description-key="interactionDescriptions:components.inputPhoto.inputPhoto.input.input2"
                />
              </label>

              <label>
                <span className={style.sliderLabel}>
                  <span>{t("label_vertical")}</span>
                  <b>{Math.round(verticalPosition)}%</b>
                </span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={verticalPosition}
                  onChange={(e) => setVerticalPosition(Number(e.target.value))}
                  data-interaction-description-key="interactionDescriptions:components.inputPhoto.inputPhoto.input.input3"
                />
              </label>
            </div>

            <div className={defaultStyle.divisionBar}></div>

            <div className={defaultStyle.buttonsController}>
              <Button
                label={t("button_use_default_crop")}
                onClick={confirmDefaultSelection}
                bgColor={THEME.colors.buttonBGPlusColor}
                descriptionKey="interactionDescriptions:components.inputPhoto.inputPhoto.button.useDefaultCrop"
              />
              <div style={{ width: "8px" }} />
              <Button
                label={t("button_apply_crop")}
                onClick={confirmSelection}
                bgColor={THEME.colors.buttonBGSaveColor}
                descriptionKey="interactionDescriptions:components.inputPhoto.inputPhoto.button.applyCrop"
              />
            </div>
          </section>
        </Animate>
      </div>
    </ModalPortal>
  )
}

const InputPhotoContent: React.FC<{
  selectedFile: File | null
  initialPhoto?: string | null
  handleOpenFileDialog: () => void
  handleImageLoad: () => void
  handleImageError: () => void
  iconColor: string
  color: string
  disable: boolean
  t: (key: string, options?: Record<string, unknown>) => string
  profileLabel: string
  removeImage: () => void
}> = ({
  selectedFile,
  initialPhoto,
  handleOpenFileDialog,
  handleImageLoad,
  handleImageError,
  iconColor,
  color,
  disable,
  removeImage,
}) => (
  <>
    {!selectedFile && !initialPhoto && !disable ? (
      <ButtonIcon
        icon={faImage}
        iconColor={iconColor}
        onClick={handleOpenFileDialog}
        bgColor={color}
        descriptionKey="interactionDescriptions:components.inputPhoto.inputPhoto.button.handleOpenFileDialog"
      />
    ) : (
      <div className={`${style.limitImage} ${style.imagePreviewFrame}`}>
        {selectedFile && (
          <div
            onClick={handleOpenFileDialog}
            className={style.selectedFile}
            data-interaction-description-key="interactionDescriptions:components.inputPhoto.inputPhoto.button.handleOpenFileDialog2"
          >
            <img className={style.image} onLoad={handleImageLoad} src={URL.createObjectURL(selectedFile)} />
          </div>
        )}

        {initialPhoto && !selectedFile && (
          <div
            onClick={handleOpenFileDialog}
            className={style.selectedFile}
            data-interaction-description-key="interactionDescriptions:components.inputPhoto.inputPhoto.button.handleOpenFileDialog3"
          >
            <img
              src={`${initialPhoto.toClientRequest()}?${new Date().getTime()}`}
              onLoad={handleImageLoad}
              onError={handleImageError}
              className={style.loaded}
            />
          </div>
        )}

        {(selectedFile || initialPhoto) && !disable && (
          <div className={style.removeImageButton}>
            <ButtonIcon
              icon={faTrashCan}
              sizeNumber={25}
              bgColor={THEME.colors.buttonBGRemoveColor}
              iconColor={THEME.colors.buttonFGRemoveColor}
              onClick={removeImage}
              descriptionKey="interactionDescriptions:components.inputPhoto.inputPhoto.button.removeImage"
            />
          </div>
        )}
      </div>
    )}
  </>
)

export const InputPhotoWithContainer: React.FC<InputPhotoProps> = ({
  initialPhoto,
  handleFileSelected,
  disable = false,
  iconColor = "black",
  color = "#444444ff",
  imageProfile = "default",
}) => {
  const { t } = useTranslation("inputPhoto")

  const profile = IMAGE_PROFILES[imageProfile]

  const inputId = useId()

  const [cropState, setCropState] = useState<CropState | null>(null)

  const [hiddenInitialImage, setHiddenInitialImage] = useState(false)

  const { selectedFile, handleImageError, handleOpenFileDialog, setSelectedFile, handleImageLoad, handleFileChange } =
    useSelectPhoto(
      handleFileSelected,
      disable,
      async (file) => {
        const src = URL.createObjectURL(file)
        setCropState({ file, src })
        return null
      },
      inputId,
    )

  useEffect(() => {
    if (initialPhoto && initialPhoto.length > 0) {
      setSelectedFile(null)
      setHiddenInitialImage(false)
    }
  }, [initialPhoto])

  const handleRemoveImage = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    setSelectedFile(null)
    setHiddenInitialImage(true)
    handleFileSelected(createRemoveImageMarker())
  }

  const handleCancelCrop = () => {
    if (cropState?.src) {
      URL.revokeObjectURL(cropState.src)
    }

    setCropState(null)
  }

  const handleConfirmCrop = (file: File | null) => {
    if (cropState?.src) {
      URL.revokeObjectURL(cropState.src)
    }

    setCropState(null)

    if (!file) {
      return
    }

    setHiddenInitialImage(false)
    setSelectedFile(file)
    handleFileSelected(file)
  }

  return (
    <>
      <TitlePageResponsiveIconSmall
        title={t("label_title_select_photo")}
        icon={faImage}
        boxShadow="none"
        iconColor={THEME.colors.buttonBGCloseColor}
        path={t("label_recommended_size", { size: profile.label })}
        children={
          <InputPhotoContent
            selectedFile={selectedFile}
            initialPhoto={hiddenInitialImage ? null : initialPhoto}
            handleOpenFileDialog={handleOpenFileDialog}
            handleImageLoad={handleImageLoad}
            handleImageError={handleImageError}
            iconColor={iconColor}
            color={color}
            disable={disable}
            t={t}
            profileLabel={profile.label}
            removeImage={handleRemoveImage}
          />
        }
      />

      <input id={inputId} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileChange} />

      {cropState && (
        <InputPhotoCropModal
          cropState={cropState}
          profile={profile}
          onCancel={handleCancelCrop}
          onConfirm={handleConfirmCrop}
        />
      )}
    </>
  )
}

export const InputPhoto: React.FC<InputPhotoProps> = ({
  initialPhoto,
  handleFileSelected,
  disable = false,
  iconButton,
  titleButton,
  iconColor = "black",
  color = "#444444ff",
  imageProfile = "default",
}) => {
  const { t } = useTranslation("inputPhoto")

  const profile = IMAGE_PROFILES[imageProfile]

  const inputId = useId()

  const [cropState, setCropState] = useState<CropState | null>(null)

  const [hiddenInitialImage, setHiddenInitialImage] = useState(false)

  const { selectedFile, handleImageError, handleOpenFileDialog, setSelectedFile, handleImageLoad, handleFileChange } =
    useSelectPhoto(
      handleFileSelected,
      disable,
      async (file) => {
        const src = URL.createObjectURL(file)
        setCropState({ file, src })
        return null
      },
      inputId,
    )

  useEffect(() => {
    if (initialPhoto && initialPhoto.length > 0) {
      setSelectedFile(null)
      setHiddenInitialImage(false)
    }
  }, [initialPhoto])

  const handleRemoveImage = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    setSelectedFile(null)
    setHiddenInitialImage(true)
    handleFileSelected(createRemoveImageMarker())
  }

  const handleCancelCrop = () => {
    if (cropState?.src) {
      URL.revokeObjectURL(cropState.src)
    }

    setCropState(null)
  }

  const handleConfirmCrop = (file: File | null) => {
    if (cropState?.src) {
      URL.revokeObjectURL(cropState.src)
    }

    setCropState(null)

    if (!file) {
      return
    }

    setHiddenInitialImage(false)
    setSelectedFile(file)
    handleFileSelected(file)
  }

  return (
    <Animate type={AnimationTypes.fadeInTraslate}>
      <div>
        <div className={style.containerPhoto}>
          <span className={style.cropSizeHint}>{t("label_recommended_size", { size: profile.label })}</span>

          {!selectedFile && !(initialPhoto && !hiddenInitialImage) && !disable ? (
            <div className={`${style.buttom} ${style.selectImageButton}`}>
              {!iconButton ? (
                <Button
                  label={titleButton ?? t("label_title_select_photo")}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleOpenFileDialog()
                  }}
                  bgColor={color}
                  descriptionKey="interactionDescriptions:components.inputPhoto.inputPhoto.button.titleSelectPhoto"
                />
              ) : (
                <ButtonIcon
                  icon={faImage}
                  iconColor={iconColor}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleOpenFileDialog()
                  }}
                  bgColor={color}
                  descriptionKey="interactionDescriptions:components.inputPhoto.inputPhoto.button.e"
                />
              )}
            </div>
          ) : (
            <div className={`${style.limitImage} ${style.imagePreviewFrame}`}>
              {selectedFile && (
                <div
                  onClick={(e) => {
                    e.stopPropagation()
                    handleOpenFileDialog()
                  }}
                  className={style.selectedFile}
                  data-interaction-description-key="interactionDescriptions:components.inputPhoto.inputPhoto.button.e2"
                >
                  <img className={style.image} onLoad={handleImageLoad} src={URL.createObjectURL(selectedFile)} />
                </div>
              )}

              {initialPhoto && !selectedFile && !hiddenInitialImage && (
                <div
                  onClick={(e) => {
                    e.stopPropagation()
                    handleOpenFileDialog()
                  }}
                  className={style.selectedFile}
                  data-interaction-description-key="interactionDescriptions:components.inputPhoto.inputPhoto.button.e3"
                >
                  <img
                    src={`${initialPhoto.toClientRequest()}?${new Date().getTime()}`}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    className={style.loaded}
                  />
                </div>
              )}

              {(selectedFile || (initialPhoto && !hiddenInitialImage)) && !disable && (
                <div className={style.removeImageButton}>
                  <ButtonIcon
                    icon={faTrashCan}
                    bgColor={THEME.colors.buttonBGRemoveColor}
                    iconColor={THEME.colors.buttonFGRemoveColor}
                    onClick={handleRemoveImage}
                    descriptionKey="interactionDescriptions:components.inputPhoto.inputPhoto.button.handleRemoveImage"
                  />
                </div>
              )}
            </div>
          )}

          <input id={inputId} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileChange} />
        </div>

        {cropState && (
          <InputPhotoCropModal
            cropState={cropState}
            profile={profile}
            onCancel={handleCancelCrop}
            onConfirm={handleConfirmCrop}
          />
        )}
      </div>
    </Animate>
  )
}
